import { VerificationStatus } from "shared/types";
import { has } from "shared/utils";
import { Loading02, Lock01 } from "untitledui-js-base";
import { CriminalOffenses } from "types/landlord/reports";
import { AuditSummaryFlag } from "views/applications-id/components/ApplicantsTable/components/AuditSummaryFlag";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import { ReportStatusBadge } from "views/applications-id/components/ApplicantsTable/components/ReportStatusBadge";
import { FormattedConvictions } from "views/applications-id/components/ApplicantsTable/components/TableBodySections/FormattedConvictions";
import {
  ApplicantOrInvitee,
  Verification
} from "views/applications-id/components/ApplicantsTable/types";
import { useAuthContext } from "providers/Auth";
import { Applicant } from "types/application";
import { HugeiconsIcon } from "@hugeicons/react";
import { MinusSignIcon } from "@hugeicons-pro/core-stroke-rounded";

const CriminalContent = ({
  requireApproval,
  hasOffenses,
  offensesData,
  offensesCount,
  hasAuditFlagEnabled,
  auditSummaryFlags,
  isDetailed = false
}: {
  requireApproval?: boolean;
  hasOffenses: boolean;
  offensesData?: CriminalOffenses;
  offensesCount?: number;
  hasAuditFlagEnabled: boolean;
  auditSummaryFlags?: Applicant["audit_summary_flags"];
  isDetailed?: boolean;
}): JSX.Element => {
  if (requireApproval) return <Lock01 className="stroke-content-100 h-5 w-5" />;

  if (hasOffenses) {
    if (isDetailed && offensesData)
      return (
        <FormattedConvictions
          all={offensesData.all}
          convicted={offensesData.convicted}
        />
      );
    return <span>{offensesCount || 0} convictions</span>;
  }

  if (hasAuditFlagEnabled && auditSummaryFlags)
    return <AuditSummaryFlag audit_summary_flags={auditSummaryFlags} />;

  return (
    <HugeiconsIcon
      icon={MinusSignIcon}
      size={20}
      className="text-content-100"
    />
  );
};

const getCheckPropValue = (
  requireApproval: boolean | undefined,
  verificationStatus: VerificationStatus,
  isApplicant: boolean,
  ruleBased: boolean | undefined,
  inProgress: boolean,
  sexOffenderCheckImportance: number | undefined,
  sexOffenderCheck: boolean | undefined
): boolean | null => {
  if (requireApproval || verificationStatus === VerificationStatus.Failed)
    return null;

  if (verificationStatus === VerificationStatus.NoDataAvailable) return null;

  if (
    isApplicant &&
    !ruleBased &&
    !inProgress &&
    typeof sexOffenderCheckImportance === "number"
  )
    return sexOffenderCheck ?? null;

  return null;
};

export const CriminalReportSection = ({
  hasClearaCategorization,
  applicant,
  isApplicant,
  verification,
  ruleBased,
  inProgress,
  hasAuditFlagEnabled
}: {
  hasAuditFlagEnabled: boolean;
  hasClearaCategorization?: boolean;
  applicant: ApplicantOrInvitee;
  isApplicant: boolean;
  ruleBased?: boolean;
  inProgress: boolean;
  verification: Verification;
}) => {
  const { permissions } = useAuthContext();
  const hasPendingCountyReports =
    verification.vendor === "cleara" && verification.has_pending_county_reports;

  const requireApproval = verification.summary?.require_approval;
  const auditSummaryFlags =
    "audit_summary_flags" in applicant
      ? applicant.audit_summary_flags
      : undefined;

  const criteriaCheckSettings =
    "criteria_check_settings" in applicant
      ? applicant.criteria_check_settings
      : undefined;

  const checkPropValue = getCheckPropValue(
    requireApproval,
    verification.status,
    isApplicant,
    ruleBased,
    inProgress,
    criteriaCheckSettings?.sex_offender_check?.importance ?? undefined,
    criteriaCheckSettings?.sex_offender_check?.check
  );

  return (
    <>
      {hasClearaCategorization ? (
        <>
          <Cell
            isFirst
            isDataCell
            className="relative"
            isPermitted={permissions.canAccessReports.usCriminalReport}
            showRestrictedFlag={!permissions.canAccessReports.usCriminalReport}
          >
            <CriminalContent
              requireApproval={requireApproval}
              hasOffenses={has(verification.summary?.total_felony_offenses)}
              offensesData={verification.summary?.total_felony_offenses}
              hasAuditFlagEnabled={hasAuditFlagEnabled}
              auditSummaryFlags={auditSummaryFlags}
              isDetailed={true}
            />
            {hasPendingCountyReports && (
              <div className="absolute top-0 right-0 text-warning-700 flex gap-2 items-center text-xs p-2 rounded-bl-lg bg-warning-50 border border-alpha-40">
                <Loading02 size="14" className="stroke-warning-700" /> Waiting
                for county search
              </div>
            )}

            <ReportStatusBadge
              verificationStatus={verification.status}
              applicantStatus={applicant.application_status}
              isRequireApproval={requireApproval}
              executionMode={verification.settings?.execution_mode}
            />
          </Cell>
          <Cell
            isDataCell
            isPermitted={permissions.canAccessReports.usCriminalReport}
          >
            <CriminalContent
              requireApproval={requireApproval}
              hasOffenses={has(
                verification.summary?.total_misdemeanor_offenses
              )}
              offensesData={verification.summary?.total_misdemeanor_offenses}
              hasAuditFlagEnabled={hasAuditFlagEnabled}
              auditSummaryFlags={auditSummaryFlags}
              isDetailed={true}
            />
          </Cell>
          <Cell
            isDataCell
            isPermitted={permissions.canAccessReports.usCriminalReport}
          >
            <CriminalContent
              requireApproval={requireApproval}
              hasOffenses={has(
                verification.summary?.total_other_criminal_offenses
              )}
              offensesData={verification.summary?.total_other_criminal_offenses}
              hasAuditFlagEnabled={hasAuditFlagEnabled}
              auditSummaryFlags={auditSummaryFlags}
              isDetailed={true}
            />
          </Cell>
        </>
      ) : (
        <>
          <Cell
            isFirst
            isDataCell
            className="relative"
            isPermitted={permissions.canAccessReports.usCriminalReport}
            showRestrictedFlag={!permissions.canAccessReports.usCriminalReport}
          >
            <CriminalContent
              requireApproval={requireApproval}
              hasOffenses={has(verification.summary?.total_criminal_offenses)}
              offensesCount={verification.summary?.total_criminal_offenses || 0}
              hasAuditFlagEnabled={hasAuditFlagEnabled}
              auditSummaryFlags={auditSummaryFlags}
              isDetailed={false}
            />

            <ReportStatusBadge
              verificationStatus={verification.status}
              applicantStatus={applicant.application_status}
              isRequireApproval={requireApproval}
              executionMode={verification.settings?.execution_mode}
            />
          </Cell>
          <Cell
            isDataCell
            isPermitted={permissions.canAccessReports.usCriminalReport}
          >
            <CriminalContent
              requireApproval={requireApproval}
              hasOffenses={has(verification.summary?.total_traffic_offenses)}
              offensesCount={verification.summary?.total_traffic_offenses || 0}
              hasAuditFlagEnabled={hasAuditFlagEnabled}
              auditSummaryFlags={auditSummaryFlags}
              isDetailed={false}
            />
          </Cell>
        </>
      )}
      <Cell
        isDataCell
        check={checkPropValue}
        isPermitted={permissions.canAccessReports.usCriminalReport}
      >
        <CriminalContent
          requireApproval={requireApproval}
          hasOffenses={has(verification.summary?.total_sex_offender_offenses)}
          offensesCount={
            verification.summary?.total_sex_offender_offenses?.all || 0
          }
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
          isDetailed={false}
        />
      </Cell>
      <Cell
        isDataCell
        isPermitted={permissions.canAccessReports.usCriminalReport}
      >
        <CriminalContent
          requireApproval={requireApproval}
          hasOffenses={has(verification.summary?.total_ofac_offenses)}
          offensesCount={verification.summary?.total_ofac_offenses || 0}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
          isDetailed={false}
        />
      </Cell>
    </>
  );
};
